/**
 * The popup shown when someone clicks a person's name.
 *
 * Identity, the public profile, contact details they chose to share, and a
 * way into a direct conversation. Opening a DM is get-or-create on the
 * server, so the button is safe to press twice.
 */

import { useCallback, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useCreateRoom, useMembers, useUser } from '@/api/queries'
import { refreshCachedRoom, usePresence } from '@/api/store'
import type { User } from '@/api/types'
import { Avatar, Button, Modal, useToast } from '@/components/ui'
import { useExternalImages, userAvatarSrc } from '@/lib/externalImages'
import { formatDay, formatRelative, formatTimeInZone } from '@/lib/format'
import { isSafeLink } from '@/lib/markdown'
import styles from './UserCard.module.css'

function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

function handleOf(user: User): string {
  return user.is_local ? `@${user.username}` : user.qualified_id
}

export interface UserCardProps {
  user: User
  open: boolean
  /**
   * The room the card was opened from, when there is one. It is what lets the
   * card say since when this person has been in the conversation you are
   * reading — the membership is already loaded for the room.
   */
  roomId?: string
  onClose: () => void
}

export function UserCard({ user, open, roomId, onClose }: UserCardProps): ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const { user: self } = useAuth()
  const presence = usePresence()
  const allowExternalImages = useExternalImages()
  const createRoom = useCreateRoom()
  const { data: fresh } = useUser(open ? user.id : undefined)
  const { data: members } = useMembers(open ? roomId : undefined)
  const profile = fresh ?? user

  const joinedAt = useMemo(
    () => (members ?? []).find((member) => member.user.id === profile.id)?.joined_at ?? null,
    [members, profile.id],
  )

  // Recomputed on every open, which is often enough for a clock that is only
  // ever accurate to the minute.
  const localTime =
    profile.timezone == null ? null : formatTimeInZone(Date.now(), profile.timezone)

  const name = personName(profile)
  const isSelf = self?.id === profile.id
  const online = presence.get(profile.id)?.status === 'online'
  const canMessage = !isSelf && !profile.is_bot && profile.status !== 'disabled'

  const bio = profile.bio?.trim() ?? ''
  const phone = profile.phone?.trim() ?? ''
  const linkedin = profile.linkedin_url?.trim() ?? ''
  const lastSeen = profile.last_seen_at

  const copyId = useCallback(() => {
    if (typeof navigator.clipboard?.writeText === 'function') {
      void navigator.clipboard.writeText(profile.id).then(
        () => toast.success('Relaii-ID kopiert til utklippstavlen!'),
        () => toast.error('Kunne ikke kopiere Relaii-ID.'),
      )
    } else {
      toast.warning('Nettleseren tillater ikke kopiering til utklippstavlen.')
    }
  }, [profile.id, toast])

  const openDirect = useCallback(async (): Promise<void> => {
    if (!canMessage) return

    try {
      const room = await createRoom.mutateAsync(
        profile.is_local
          ? { kind: 'direct', member_ids: [profile.id] }
          : { kind: 'direct', qualified_ids: [profile.qualified_id] },
      )
      refreshCachedRoom(room)
      onClose()
      navigate(`/rooms/${room.id}`)
    } catch (error) {
      toast.error(describe(error, `Could not open a conversation with ${name}.`))
    }
  }, [canMessage, createRoom, name, navigate, onClose, profile, toast])

  return (
    <Modal open={open} onClose={onClose} title={name} size="sm">
      <div className={styles.card}>
        <div className={styles.identity}>
          <Avatar
            name={name}
            id={profile.id}
            src={userAvatarSrc(profile, allowExternalImages)}
            size="lg"
            presence={online ? 'online' : 'offline'}
          />
          <div className={styles.who}>
            <p className={styles.name}>{name}</p>
            <p className={styles.handle}>{handleOf(profile)}</p>
            <p className={styles.presence}>
              {online
                ? 'Online'
                : lastSeen
                  ? `Last seen ${formatRelative(lastSeen)}`
                  : 'Offline'}
            </p>
            {isSelf || profile.is_bot || profile.is_admin || !profile.is_local ? (
              <div className={styles.pills}>
                {isSelf ? <span className={styles.pill}>You</span> : null}
                {profile.is_bot ? <span className={styles.pill}>Bot</span> : null}
                {profile.is_admin ? <span className={styles.pill}>Admin</span> : null}
                {!profile.is_local ? (
                  <>
                    <span className={styles.pill}>{profile.server_id}</span>
                    <span className={styles.pill}>Guest in this Space</span>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {canMessage ? (
          <Button
            variant="primary"
            fullWidth
            loading={createRoom.isPending}
            onClick={() => void openDirect()}
          >
            Send message
          </Button>
        ) : null}

        <section className={styles.section} aria-labelledby={`user-card-info-${profile.id}`}>
          <h3 className={styles.heading} id={`user-card-info-${profile.id}`}>
            Info
          </h3>
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt>Relaii-ID</dt>
              <dd className={styles.copyableFact}>
                <span className={styles.monoId}>{profile.id}</span>
                <button
                  type="button"
                  className={styles.copyIdButton}
                  onClick={copyId}
                  title="Kopier Relaii-ID"
                >
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
                    <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M3.5 10.5H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h6.5a1 1 0 0 1 1 1v.5" stroke="currentColor" strokeWidth="1.3" />
                  </svg>
                  Kopier
                </button>
              </dd>
            </div>
          </dl>
          {bio !== '' ? (
            <p className={styles.bio}>{bio}</p>
          ) : (
            <p className={styles.empty}>No bio yet.</p>
          )}
        </section>

        {joinedAt !== null || localTime !== null ? (
          <section className={styles.section} aria-labelledby={`user-card-where-${profile.id}`}>
            <h3 className={styles.heading} id={`user-card-where-${profile.id}`}>
              In this Space
            </h3>
            <dl className={styles.facts}>
              {joinedAt !== null ? (
                <div className={styles.fact}>
                  <dt>In this channel</dt>
                  <dd>{formatDay(joinedAt)}</dd>
                </div>
              ) : null}
              {localTime !== null ? (
                <div className={styles.fact}>
                  <dt>Local time</dt>
                  <dd>
                    {localTime}
                    <span aria-hidden="true"> · </span>
                    {profile.timezone}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        ) : null}

        <section className={styles.section} aria-labelledby={`user-card-contact-${profile.id}`}>
          <h3 className={styles.heading} id={`user-card-contact-${profile.id}`}>
            Contact
          </h3>
          {phone === '' && linkedin === '' ? (
            <p className={styles.empty}>No contact details shared.</p>
          ) : (
            <dl className={styles.facts}>
              {phone !== '' ? (
                <div className={styles.fact}>
                  <dt>Phone</dt>
                  <dd>
                    <a href={`tel:${phone.replace(/\s+/gu, '')}`}>{phone}</a>
                  </dd>
                </div>
              ) : null}
              {linkedin !== '' ? (
                <div className={styles.fact}>
                  <dt>LinkedIn</dt>
                  <dd>
                    {/* The server validates the scheme today; the same gate the
                        message renderer uses keeps that true of older rows too. */}
                    {isSafeLink(linkedin) ? (
                      <a href={linkedin} target="_blank" rel="noreferrer">
                        {linkedin.replace(/^https?:\/\/(www\.)?/u, '')}
                      </a>
                    ) : (
                      linkedin
                    )}
                  </dd>
                </div>
              ) : null}
            </dl>
          )}
        </section>
      </div>
    </Modal>
  )
}

export interface UserNameButtonProps {
  user: User
  /** Passed through so the card can say since when they have been in it. */
  roomId?: string
  className?: string
  title?: string
  children: ReactNode
}

/** A name (or any label) that opens the person's card. */
export function UserNameButton({
  user,
  roomId,
  className,
  title,
  children,
}: UserNameButtonProps): ReactElement {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        className={className}
        title={title}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>
      <UserCard user={user} open={open} roomId={roomId} onClose={() => setOpen(false)} />
    </>
  )
}
